import type { PlanKey } from "@giftforward/entitlements";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { recipientCapCopy } from "@/constants/GfgModal";

interface RecipientLimitDialogProps {
  open: boolean;
  cap: number;
  planKey: PlanKey;
  onClose: () => void;
}

/**
 * "Contact limit reached" modal (GFG-439) — shown instead of the add wizards once a launched
 * campaign is at its plan's recipient cap, or when the server refuses an addition for it. "View
 * plans" reuses the Account deep-link (see interfaces/accountNav.ts), like SeatLimitDialog.
 */
export default function RecipientLimitDialog({
  open,
  cap,
  planKey,
  onClose,
}: RecipientLimitDialogProps) {
  const navigate = useNavigate();
  const copy = recipientCapCopy(planKey, cap).blocked;

  function handleViewPlans() {
    onClose();
    navigate("/account", { state: { tab: "plan", nonce: Date.now() } });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            width: 480,
            maxWidth: "calc(100vw - 40px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            position: "relative",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          border: "1px solid var(--color-smoke)",
          borderRadius: "var(--radius-btn)",
          color: "var(--color-graphite)",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Typography
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: "24px",
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          mb: "6px",
        }}
      >
        {copy.title}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)" }}>
        {copy.description}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", mt: "22px" }}>
        <Button
          onClick={onClose}
          disableRipple
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: "var(--color-rodeo-black)",
            bgcolor: "var(--color-white)",
            border: "1px solid var(--color-smoke)",
            borderRadius: "6px",
            px: "20px",
            py: "10px",
            "&:hover": { borderColor: "var(--color-dark-dust)", bgcolor: "var(--color-white)" },
          }}
        >
          {copy.secondaryLabel}
        </Button>
        <Button
          onClick={handleViewPlans}
          disableRipple
          disableElevation
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            bgcolor: "var(--color-rodeo-black)",
            color: "var(--color-white)",
            borderRadius: "6px",
            px: "20px",
            py: "10px",
            "&:hover": { bgcolor: "#222" },
          }}
        >
          View plans
        </Button>
      </Box>
    </Dialog>
  );
}
